import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard, StatCardSkeleton } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Visibility, FileDownload, Search, CheckCircleOutline, AccountBalanceWallet, Receipt } from '@mui/icons-material';
import { useTransactions, useTransactionSummary, usePermission } from '../hooks';
import { colors, transitions, radius } from '../theme/theme';
import { useMutation } from '@tanstack/react-query';
import { transactionService } from '../services';
import { useSnackbar } from '../contexts/SnackbarContext';

export const TransactionsPage = () => {
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed' | 'reversed'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);

  const { can } = usePermission();
  const { transactions, isLoading, error } = useTransactions({
    page: page + 1,
    per_page: rowsPerPage,
    search: search || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    payment_method: paymentMethodFilter !== 'all' ? paymentMethodFilter : undefined,
  });

  const { summary, isLoading: summaryLoading } = useTransactionSummary();

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: () =>
      transactionService.exportTransactions({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        payment_method: paymentMethodFilter !== 'all' ? paymentMethodFilter : undefined,
      }),
    onSuccess: (data) => {
      // Create blob with proper CSV type
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Export completed successfully', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to export transactions';
      showSnackbar(message, 'error');
    },
  });

  const handleViewTransaction = (transaction: any) => {
    setSelectedTransaction(transaction);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'successful':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Calculate statistics from summary API
  const totalTransactions = summary?.total || 0;
  const completedTransactions = summary?.successful || 0;
  const totalAmount = summary?.total_amount || 0;

  const columns: GridColDef[] = [
    {
      field: 'reference',
      headerName: 'Reference',
      width: 130,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      width: 140,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.customer ? `${params.row.customer.first_name} ${params.row.customer.last_name}` : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 150,
      display: 'flex',
      renderCell: (params) => {
        const bill = params.row.bill;
        const service = bill?.service?.name || params.row.charge?.service?.name || params.row.service?.name;
        const desc = bill?.description || service || 'N/A';
        return (
          <Typography variant="body2" noWrap title={desc}>
            {desc}
          </Typography>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          KES {parseFloat(params.value || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'touch_point_id',
      headerName: 'Channel',
      width: 120,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" noWrap sx={{ textTransform: 'capitalize' }}>
          {(params.row.touch_point?.touch_point || 'N/A').replace(/_/g, ' ')}
        </Typography>
      ),
    },
    {
      field: 'payment_method',
      headerName: 'Payment',
      width: 90,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 95,
      display: 'flex',
      valueGetter: (value: any) => new Date(value).toLocaleDateString(),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Transactions"
          subtitle="View and manage all transactions"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Transactions' },
          ]}
          action={can('transactions.export') ?
            <Button
              variant="contained"
              startIcon={exportMutation.isPending ? <CircularProgress size={20} /> : <FileDownload />}
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {exportMutation.isPending ? 'Exporting...' : 'Export'}
            </Button> : undefined
          }
        />

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {summaryLoading ? (
            <StatCardSkeleton count={3} />
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatCard
                  title="Total Transactions"
                  value={totalTransactions}
                  icon={<Receipt />}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatCard
                  title="Completed"
                  value={completedTransactions}
                  icon={<CheckCircleOutline />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatCard
                  title="Total Amount"
                  value={`KES ${totalAmount.toLocaleString()}`}
                  icon={<AccountBalanceWallet />}
                  color={colors.secondary.main}
                />
              </Grid>
            </>
          )}
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load transactions. Please try again.
          </Alert>
        )}

        <Card sx={{
          borderRadius: 2,
          overflow: 'visible',
          boxShadow: colors.shadows.base,
          border: `1px solid ${colors.grey[200]}`,
          background: colors.gradients.card,
        }}>
          <CardContent>
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <TextField
                fullWidth
                size="small"
                placeholder="Search by reference, customer name, M-Pesa receipt..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ flex: 1, minWidth: 200 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethodFilter}
                  onChange={(e) => setPaymentMethodFilter(e.target.value)}
                  label="Payment Method"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="mpesa">M-Pesa</MenuItem>
                  <MenuItem value="bank">Bank</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={transactions?.data || []}
                columns={[
                  ...columns,
                  {
                    field: 'actions',
                    headerName: '',
                    width: 50,
                    sortable: false,
                    display: 'flex',
                    renderCell: (params) => (
                      <IconButton
                        size="small"
                        onClick={() => handleViewTransaction(params.row)}
                        sx={{ color: colors.primary.main }}
                      >
                        <Visibility sx={{ fontSize: 18 }} />
                      </IconButton>
                    ),
                  },
                ]}
                loading={isLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationModel={{ page, pageSize: rowsPerPage }}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setRowsPerPage(model.pageSize);
                }}
                rowCount={transactions?.meta?.total || transactions?.total || 0}
                paginationMode="server"
                disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    borderRadius: 2,
                    '& .MuiDataGrid-main': {
                      borderRadius: 2,
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                    },
                    '& .MuiDataGrid-columnHeader:first-of-type': {
                      borderTopLeftRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeader:last-of-type': {
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderBottomLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                    },
                  }}
                />
              </Box>
          </CardContent>
        </Card>

        {/* View Transaction Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: colors.shadows.xl,
            }
          }}
        >
          {selectedTransaction && (
            <>
              {/* Header with amount and status */}
              <Box sx={{ px: 3, pt: 3, pb: 2, textAlign: 'center', bgcolor: colors.grey[50] }}>
                <Chip
                  label={selectedTransaction.status}
                  color={getStatusColor(selectedTransaction.status)}
                  size="small"
                  sx={{ textTransform: 'capitalize', mb: 1.5 }}
                />
                <Typography variant="h4" fontWeight={700}>
                  KES {parseFloat(selectedTransaction.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {new Date(selectedTransaction.created_at).toLocaleString()}
                </Typography>
              </Box>

              <DialogContent sx={{ px: 3, py: 2 }}>
                {/* Description */}
                <Box sx={{ mb: 2, p: 2, bgcolor: colors.grey[50], borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedTransaction.bill?.description || selectedTransaction.bill?.service?.name || selectedTransaction.charge?.service?.name || selectedTransaction.service?.name || 'N/A'}
                  </Typography>
                  {selectedTransaction.bill?.invoice_number && (
                    <Typography variant="caption" color="text.secondary">
                      Invoice: {selectedTransaction.bill.invoice_number}
                    </Typography>
                  )}
                </Box>

                {/* Details Grid */}
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Transaction Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Reference</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                      {selectedTransaction.reference}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Customer</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedTransaction.customer
                        ? `${selectedTransaction.customer.first_name} ${selectedTransaction.customer.last_name}`
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Payment Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                      {selectedTransaction.payment_method || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Collection Channel</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                      {(selectedTransaction.touch_point?.touch_point || 'N/A').replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                  {selectedTransaction.payment_module && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Payment Gateway</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedTransaction.payment_module.name}
                      </Typography>
                    </Box>
                  )}
                  {selectedTransaction.mpesa_receipt_number && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">M-Pesa Receipt</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedTransaction.mpesa_receipt_number}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* M-Pesa specific details */}
                {selectedTransaction.mpesa_transaction_id && (
                  <>
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      M-Pesa Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">M-Pesa Transaction ID</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedTransaction.mpesa_transaction_id}</Typography>
                      </Box>
                      {selectedTransaction.mpesa_checkout_request_id && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Checkout Request ID</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{selectedTransaction.mpesa_checkout_request_id}</Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {/* Zone & Service Details */}
                {(selectedTransaction.charge?.zone?.name || selectedTransaction.touch_point?.touch_point) && (
                  <>
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Service Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                      {selectedTransaction.charge?.zone?.name && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Zone</Typography>
                          <Typography variant="body2" fontWeight={600}>{selectedTransaction.charge.zone.name}</Typography>
                        </Box>
                      )}
                      {selectedTransaction.charge?.service?.name && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Service</Typography>
                          <Typography variant="body2" fontWeight={600}>{selectedTransaction.charge.service.name}</Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {/* Dynamic Fields (user-entered data) */}
                {selectedTransaction.fields && typeof selectedTransaction.fields === 'object' && Object.keys(selectedTransaction.fields).length > 0 && (
                  <>
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Payment Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                      {Object.entries(selectedTransaction.fields)
                        .filter(([k, v]: [string, any]) => v && String(v) !== 'N/A' && !['asset_id','module_id','zone_id','service_id'].includes(k) && typeof v !== 'object')
                        .map(([key, value]: [string, any]) => (
                          <Box key={key}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              {key.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>{String(value)}</Typography>
                          </Box>
                        ))}
                    </Box>
                  </>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCloseDialog} variant="outlined">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
